/**
 * Oath Payment Account Service
 * API calls for payment account management
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// Payment Account Endpoints
// ============================================

/**
 * Get all payment accounts for an agency
 * @param {number} agencyId - The agency ID
 * @returns {Promise<{success: boolean, accounts?: Array, error?: string}>}
 */
export const getPaymentAccounts = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}api/agencies/${agencyId}/payment-accounts`);

    if (!response.ok) {
      if (response.status === 404 || response.status === 204) {
        return { success: true, accounts: [] };
      }
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch payment accounts' };
    }

    const accounts = await parseResponse(response);
    if (!accounts || (Array.isArray(accounts) && accounts.length === 0)) {
      return { success: true, accounts: [] };
    }
    return { success: true, accounts: Array.isArray(accounts) ? accounts : [] };
  } catch (error) {
    console.error('Error fetching payment accounts:', error);
    return { success: false, error: 'Network error fetching payment accounts' };
  }
};

/**
 * Get a single payment account (masked)
 * @param {number} agencyId - The agency ID
 * @param {number} accountId - The account ID
 * @returns {Promise<{success: boolean, account?: Object, error?: string}>}
 */
export const getPaymentAccount = async (agencyId, accountId) => {
  try {
    const response = await authFetch(`${BASE_URL}api/agencies/${agencyId}/payment-accounts/${accountId}`);

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to fetch payment account' };
    }

    const account = await parseResponse(response);
    return { success: true, account };
  } catch (error) {
    console.error('Error fetching payment account:', error);
    return { success: false, error: 'Network error fetching payment account' };
  }
};

/**
 * Create a new payment account
 * @param {number} agencyId - The agency ID
 * @param {Object} accountData - Payment account data
 * @param {string} accountData.routingNumber - 9-digit routing number
 * @param {string} accountData.accountNumber - 4-17 digit account number
 * @param {string} accountData.nameOnAccount - Name on the account
 * @param {string} accountData.nickName - Display nickname
 * @param {string} accountData.accountType - "Checking" or "Savings"
 * @param {string} [accountData.defaultPaymentType] - "NET", "BUF", or "BOTH"
 * @returns {Promise<{success: boolean, account?: Object, error?: string}>}
 */
export const createPaymentAccount = async (agencyId, accountData) => {
  try {
    const response = await authFetch(`${BASE_URL}api/agencies/${agencyId}/payment-accounts`, {
      method: 'POST',
      body: JSON.stringify({ agencyId, ...accountData }),
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to create payment account' };
    }

    const account = await parseResponse(response);
    return { success: true, account };
  } catch (error) {
    console.error('Error creating payment account:', error);
    return { success: false, error: 'Network error creating payment account' };
  }
};

/**
 * Update an existing payment account
 * @param {number} agencyId - The agency ID
 * @param {number} accountId - The account ID
 * @param {Object} accountData - Fields to update (all optional)
 * @param {string} [accountData.routingNumber] - 9-digit routing number
 * @param {string} [accountData.accountNumber] - 4-17 digit account number
 * @param {string} [accountData.nameOnAccount] - Name on the account
 * @param {string} [accountData.nickName] - Display nickname
 * @param {boolean} [accountData.active] - Active status
 * @param {string} [accountData.defaultPaymentType] - "NET", "BUF", or "BOTH"
 * @returns {Promise<{success: boolean, account?: Object, error?: string}>}
 */
export const updatePaymentAccount = async (agencyId, accountId, accountData) => {
  try {
    const response = await authFetch(`${BASE_URL}api/agencies/${agencyId}/payment-accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify(accountData),
    });

    if (!response.ok) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to update payment account' };
    }

    const account = await parseResponse(response);
    return { success: true, account };
  } catch (error) {
    console.error('Error updating payment account:', error);
    return { success: false, error: 'Network error updating payment account' };
  }
};

/**
 * Look up routing numbers by prefix from the FedACH directory
 * @param {number} agencyId - The agency ID
 * @param {string} prefix - Routing number prefix (4+ digits)
 * @returns {Promise<{success: boolean, results?: Array<{routingNumber: string, bankName: string}>, error?: string}>}
 */
export const lookupRoutingNumber = async (agencyId, prefix) => {
  try {
    const response = await authFetch(
      `${BASE_URL}api/agencies/${agencyId}/payment-accounts/routing-lookup?prefix=${encodeURIComponent(prefix)}`
    );

    if (!response.ok) {
      return { success: false, error: 'Failed to look up routing numbers' };
    }

    const results = await parseResponse(response);
    return { success: true, results: Array.isArray(results) ? results : [] };
  } catch (error) {
    console.error('Error looking up routing numbers:', error);
    return { success: false, error: 'Network error looking up routing numbers' };
  }
};

/**
 * Deactivate (soft-delete) a payment account
 * @param {number} agencyId - The agency ID
 * @param {number} accountId - The account ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deactivatePaymentAccount = async (agencyId, accountId) => {
  try {
    const response = await authFetch(`${BASE_URL}api/agencies/${agencyId}/payment-accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify({ active: false }),
    });

    if (!response.ok && response.status !== 204) {
      const errorData = await parseResponse(response);
      return { success: false, error: errorData.message || 'Failed to deactivate payment account' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error deactivating payment account:', error);
    return { success: false, error: 'Network error deactivating payment account' };
  }
};
